if (typeof SwatchContainer !== 'function') {
    const SWATCH_SELECTOR = '.product-item-swatch';

    class SwatchContainer extends HTMLElement {
        constructor() {
            super();
            this.cache = new Map();
        }

        connectedCallback() {
            if (this._initialized) return;
            this._initialized = true;

            this.productItem = this.closest('.product-item');
            this.imageContainer = this.productItem?.querySelector('[data-js-product-item-image]');
            if (!this.productItem || !this.imageContainer) return;

            this._onChange = (event) => {
                const input = event.target;
                if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

                if (document.activeElement !== input) {
                    input.focus({ preventScroll: true });
                }

                const swatch = input.closest(SWATCH_SELECTOR);
                if (!swatch || !input.checked) return;

                this.selectSwatch(swatch);
            };

            this._onClick = (event) => {
                if (event.target.closest(SWATCH_SELECTOR)) {
                    event.stopPropagation();
                }
            };

            this.addEventListener('change', this._onChange);
            this.addEventListener('click', this._onClick);

            const prefetchEvent = 'ontouchstart' in window ? 'touchstart' : 'mouseenter';
            for (const swatch of this.querySelectorAll(SWATCH_SELECTOR)) {
                swatch.addEventListener(prefetchEvent, () => this.prefetch(swatch), { once: true, passive: true });
            }

            const checkedInput = this.querySelector('input[type="radio"]:checked');
            if (checkedInput) {
                const swatch = checkedInput.closest(SWATCH_SELECTOR);
                if (swatch) {
                    this.updateColorLabel(swatch);
                    this.updateProductCard(swatch);
                }
            }

            this.limitSwatches();
            this._resizeHandler = debounce(() => this.limitSwatches(), 300);
            window.addEventListener('resize', this._resizeHandler);
        }

        disconnectedCallback() {
            if (this._onChange) this.removeEventListener('change', this._onChange);
            if (this._onClick) this.removeEventListener('click', this._onClick);
            if (this._resizeHandler) {
                window.removeEventListener('resize', this._resizeHandler);
            }
        }

        getFetchUrl(swatch) {
            const fetchAttr = swatch.dataset.fetch;
            if (!fetchAttr) return null;

            return new URL(fetchAttr, `${window.location.origin}${Shopify.routes.root}`);
        }

        selectSwatch(swatch) {
            this.updateLinks(swatch);
            this.fetchProduct(swatch);
            this.updateColorLabel(swatch);
            this.updateProductCard(swatch);
        }

        updateLinks(swatch) {
            const fetchUrl = this.getFetchUrl(swatch);
            if (!fetchUrl) return;

            const links = this.productItem.querySelectorAll('a');
            const modal = this.productItem.querySelector('product-modal-toggle');
            const url = new URL(fetchUrl);
            url.searchParams.delete('sections');

            modal.setAttribute('data-product-url', url.toString());
            for (const link of links) {
                link.setAttribute('href', url);
            }
        }

        updateColorLabel(swatch) {
            const colorLabel = this.productItem.querySelector('[data-js-color-label]');
            if (colorLabel) {
                colorLabel.textContent = swatch.dataset.value;
            }
        }

        updateProductCard(swatch) {
            const value = swatch.dataset.value;
            if (!value) return;

            this.productItem.setAttribute('color', value);
            const swatchColor = swatch.querySelector('[data-js-swatch-color]')?.dataset.jsSwatchColor;
            if (swatchColor) {
                this.productItem.style.setProperty('--swatch-color', swatchColor);
            }
        }

        async prefetch(swatch) {
            const fetchUrl = this.getFetchUrl(swatch);
            if (!fetchUrl || this.cache.has(fetchUrl)) return;

            try {
                const response = await fetch(fetchUrl);
                const text = await response.text();
                const content = this.parseHTML(text);
                this.cache.set(fetchUrl, content);
            } catch {
                // Silent fail
            }
        }

        async fetchProduct(swatch) {
            const fetchUrl = this.getFetchUrl(swatch);
            if (!fetchUrl) return;

            if (this.cache.has(fetchUrl)) {
                this.updateCardContent(this.cache.get(fetchUrl));
                return;
            }

            try {
                const response = await fetch(fetchUrl);
                const text = await response.text();
                const content = this.parseHTML(text);

                this.cache.set(fetchUrl, content);
                this.updateCardContent(content);
            } catch (error) {
                console.error('Swatch fetch failed:', error);
            }
        }

        updateCardContent(content) {
            this.updateImages(content);
            this.updatePrice(content);
            this.updateBadges(content);
        }

        updateImages(content) {
            const newImage = content.querySelector('[data-js-product-item-image]');
            if (!newImage) return;
            this.imageContainer.innerHTML = newImage.innerHTML;
        }

        updateBadges(content) {
            const currentBadges = this.productItem.querySelector('.product-item__badges');
            if (!currentBadges) return;

            const newBadges = content.querySelector('.product-item__badges');
            if (!newBadges) {
                currentBadges.innerHTML = '';
                return;
            }

            currentBadges.innerHTML = newBadges.innerHTML;
        }

        updatePrice(content) {
            const price = this.productItem.querySelector('[data-js-product-item-price]');
            const newPrice = content.querySelector('[data-js-product-item-price]');
            price.innerHTML = newPrice.innerHTML;
        }

        parseHTML(text) {
            const json = JSON.parse(text);
            text = json['helper-variant-image'] || json['helper-variant-image-collection'];

            const doc = new DOMParser().parseFromString(text, 'text/html');
            return doc.querySelector('.shopify-section');
        }

        limitSwatches() {
            const isMobile = window.innerWidth <= 768;
            this.maxSwatches = this.dataset.limit ? this.dataset.limit : isMobile ? 5 : 8;
            this.swatches = this.querySelectorAll(SWATCH_SELECTOR);

            for (const [index, swatch] of this.swatches.entries()) {
                const input = swatch.querySelector('input[type="radio"]');

                if (index >= this.maxSwatches) {
                    swatch.style.display = 'none';
                    input?.setAttribute('disabled', '');
                } else {
                    swatch.style.display = 'block';
                    input?.removeAttribute('disabled');
                }
            }

            if (this.swatches.length > this.maxSwatches) {
                const existing = this.querySelector('.swatch-more');
                if (!existing) {
                    const remaining = this.swatches.length - this.maxSwatches;
                    const moreText = document.createElement('span');
                    moreText.className = 'swatch-more';
                    moreText.textContent = `+ ${remaining}`;
                    this.appendChild(moreText);
                } else {
                    const remaining = this.swatches.length - this.maxSwatches;
                    existing.textContent = `+ ${remaining}`;
                }
            }
        }
    }

    customElements.define('swatch-container', SwatchContainer);
}
